import { IAuthService } from '../../domain/interfaces/IAuthService';
import { LoginCommand } from '../../application/dto/auth/LoginCommand';
import { RegisterUserCommand } from '../../application/dto/auth/RegisterUserCommand';
import { ForgotPasswordCommand } from '../../application/dto/auth/ForgotPasswordCommand';
import { ResetPasswordCommand } from '../../application/dto/auth/ResetPasswordCommand';
import { LoginResult, RefreshTokenResult } from '../../application/dto/auth/LoginResult';
import { PasswordResetResult } from '../../application/dto/auth/PasswordResetResult';
import { CurrentUser } from '../../domain/entities/User';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';
import { extractBusinessId } from './jwtUtils';

/**
 * AuthService - Implementation of authentication operations
 *
 * Handles all authentication-related API calls
 */
export class AuthService implements IAuthService {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Login with username/email and password
   */
  async login(command: LoginCommand): Promise<LoginResult> {
    const result = await this.apiClient.post<LoginResult>(API_ENDPOINTS.AUTH.LOGIN, command);

    // Store the access token (refresh token is in httpOnly cookie, not in response body)
    if (result.succeeded && result.accessToken) {
      this.apiClient.setAuthToken(result.accessToken);
    }

    return result;
  }

  /**
   * Register a new user
   */
  async register(command: RegisterUserCommand): Promise<LoginResult> {
    // Backend returns RegisterResult, but it has the same structure as LoginResult
    // Disable toast - errors will be displayed on the form itself
    const result = await this.apiClient.post<LoginResult>(API_ENDPOINTS.AUTH.REGISTER, command, { showErrorToast: false });

    // Store the access token (refresh token is in httpOnly cookie, not in response body)
    if (result.succeeded && result.accessToken) {
      this.apiClient.setAuthToken(result.accessToken);
    }

    return result;
  }

  /**
   * Check if an email address is available for registration.
   * SESSION_194: Added reCAPTCHA token for email enumeration protection.
   */
  async checkEmailAvailability(
    email: string,
    recaptchaToken?: string
  ): Promise<{ isAvailable: boolean; message?: string }> {
    const params = new URLSearchParams({ email });
    if (recaptchaToken) {
      params.append('recaptchaToken', recaptchaToken);
    }
    const result = await this.apiClient.get<{ isAvailable: boolean; message?: string }>(
      `${API_ENDPOINTS.AUTH.CHECK_EMAIL}?${params.toString()}`,
      { showErrorToast: false }
    );
    return result;
  }

  /**
   * Logout current user
   * Backend reads access token from Authorization header and refresh token from httpOnly cookie
   */
  async logout(): Promise<void> {
    try {
      // Backend doesn't need a body - it reads token from header and cookie
      await this.apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } finally {
      // Always clear token, even if API call fails
      // Backend clears the httpOnly cookie automatically
      this.apiClient.clearAuthToken();
    }
  }

  /**
   * Refresh access token
   * Backend reads refresh token from httpOnly cookie (not from request body)
   */
  async refreshToken(accessToken: string): Promise<RefreshTokenResult> {
    const result = await this.apiClient.post<RefreshTokenResult>(
      API_ENDPOINTS.AUTH.REFRESH_TOKEN,
      { accessToken, refreshToken: '' }
    );

    if (result.succeeded && result.accessToken) {
      this.apiClient.setAuthToken(result.accessToken);
    }

    return result;
  }

  /**
   * Get current authenticated user
   * Backend /verify endpoint returns user info from JWT claims
   * SESSION_204: Added firstName, lastName, emailConfirmed fields
   */
  async getCurrentUser(): Promise<CurrentUser> {
    interface VerifyResponse {
      authenticated: boolean;
      userId: string;
      userName: string;
      email: string;
      firstName?: string | null;
      lastName?: string | null;
      roles: string[];
      authMethod: string;
      emailConfirmed?: boolean;
    }

    const response = await this.apiClient.get<VerifyResponse>(API_ENDPOINTS.AUTH.VERIFY);

    // Extract businessId from stored JWT token
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    const businessId = token ? extractBusinessId(token) : null;

    // SESSION_204: Compute displayName from firstName + lastName, fallback to email
    const firstName = response.firstName ?? null;
    const lastName = response.lastName ?? null;
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const displayName = fullName || response.email;

    // Map backend response to CurrentUser format
    return {
      id: response.userId,
      email: response.email,
      userName: response.userName,
      firstName,
      lastName,
      displayName,
      role: (response.roles?.[0] as 'Admin' | 'BusinessOwner') ?? 'BusinessOwner', // Take first role
      businessId: businessId, // Extract from JWT token claims
      emailConfirmed: response.emailConfirmed ?? false
    };
  }

  /**
   * Check if user is authenticated (has token)
   */
  isAuthenticated(): boolean {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    return !!token;
  }

  /**
   * Request password reset email
   * Backend sends reset email to user with token
   */
  async forgotPassword(command: ForgotPasswordCommand): Promise<PasswordResetResult> {
    const result = await this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      command
    );

    return result;
  }

  /**
   * Reset password with token from email
   */
  async resetPassword(command: ResetPasswordCommand): Promise<PasswordResetResult> {
    const result = await this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      command
    );

    return result;
  }
}
